import { Head } from "@inertiajs/react"
import { Users, Search, Mail, MapPin } from "lucide-react"
import { useMemo, useState } from "react"
import { MemberLayout } from "@/pages/App/Member/components/MemberLayout"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// Read-only member directory (#651): the people in the groups of the active
// group's coaches, one card per group, coaches first. The server decides who
// is in scope (MemberDirectory); this page only filters what it was sent.

function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("")
}

function Avatar({ person }) {
  if (person.profileImageUrl) {
    return (
      <img
        src={person.profileImageUrl}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full object-cover bg-zinc-100"
      />
    )
  }
  return (
    <div
      className="h-10 w-10 shrink-0 rounded-full bg-kf-red/10 text-kf-red flex items-center justify-center text-sm font-semibold"
      aria-hidden
    >
      {initials(person.name)}
    </div>
  )
}

function location(person) {
  return [person.city, person.state, person.country].filter(Boolean).join(", ")
}

function matches(person, query) {
  if (!query) return true
  return [person.name, person.company].some((value) => value?.toLowerCase().includes(query))
}

function PersonRow({ person }) {
  const role = [person.jobTitle, person.company].filter(Boolean).join(" · ")
  const place = location(person)

  return (
    <TableRow className="align-top">
      <TableCell className="whitespace-normal">
        <div className="flex gap-3">
          <Avatar person={person} />
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-zinc-900">{person.name}</span>
              {person.coach && (
                <Badge variant="secondary" className="bg-kf-red/10 text-kf-red border-kf-red/20">
                  Coach
                </Badge>
              )}
              {person.viewer && <Badge variant="outline">You</Badge>}
            </div>
            {role && <p className="text-sm text-zinc-600">{role}</p>}
            <a
              href={`mailto:${person.email}`}
              className="text-xs text-zinc-500 hover:text-kf-red inline-flex items-center gap-1 md:hidden"
            >
              <Mail className="h-3 w-3 shrink-0" />
              {person.email}
            </a>
            {place && (
              <p className="text-xs text-zinc-500 inline-flex items-center gap-1 sm:hidden">
                <MapPin className="h-3 w-3 shrink-0" />
                {place}
              </p>
            )}
            {person.bio && (
              <p className="text-sm text-zinc-500 line-clamp-3 max-w-prose">{person.bio}</p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <a
          href={`mailto:${person.email}`}
          className="text-sm text-zinc-700 hover:text-kf-red inline-flex items-center gap-1.5"
        >
          <Mail className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
          {person.email}
        </a>
      </TableCell>
      <TableCell className="hidden sm:table-cell text-sm text-zinc-600">
        {place || <span className="text-zinc-400">—</span>}
      </TableCell>
    </TableRow>
  )
}

export default function DirectoryIndex({ currentUser, groups }) {
  const [search, setSearch] = useState("")
  const query = search.trim().toLowerCase()

  const filteredGroups = useMemo(
    () =>
      groups
        .map((group) => ({ ...group, people: group.people.filter((p) => matches(p, query)) }))
        .filter((group) => group.people.length > 0),
    [groups, query]
  )

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title="Directory - Kingdom Factor" />

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Directory</h1>
          <p className="text-zinc-600 mt-1">
            The people in your coach&apos;s groups, filed by group.
          </p>
        </div>

        {groups.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium">No one to show yet</p>
          </div>
        ) : (
          <>
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                type="search"
                placeholder="Search by name or company..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search the directory by name or company"
              />
            </div>

            {filteredGroups.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium">No matches</p>
                <p className="text-sm">Try a different search.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredGroups.map((group) => (
                  <Card key={group.id} className="py-0 gap-0 overflow-hidden shadow-sm">
                    <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-4 sm:px-6">
                      <h2 className="text-lg font-semibold text-zinc-900">{group.name}</h2>
                      {group.active && (
                        <Badge variant="outline" className="text-xs">Your group</Badge>
                      )}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-zinc-500">Name</TableHead>
                          <TableHead className="text-zinc-500 hidden md:table-cell">Email</TableHead>
                          <TableHead className="text-zinc-500 hidden sm:table-cell">Location</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.people.map((person) => (
                          <PersonRow key={`${group.id}-${person.id}`} person={person} />
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </MemberLayout>
  )
}
